import { PayloadAction } from '@reduxjs/toolkit';
import { call, delay, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { api } from 'services/api';
import {
	TClientAccess,
	TDeleteMethodResponse,
	TDirectDebitBanks,
	TDirectDebitCreateResponse,
	TDirectDebitVerifyResult,
	TPaymentMethods,
	TPaymentMethodSetup,
	TPaymentMethodsSummary,
	TPaymentServices,
	TSetupResult,
} from 'services/api/paymentMethods/types';
import { getMerchantId } from 'redux/reducers/auth/selectors';
import { notificationContainer } from 'utils/notificationContainer';
import { getErrorMessage } from 'utils/apiError';
import {
	SETUP_COMPLETED_MESSAGE,
	SETUP_DUPLICATE_MESSAGE,
	SETUP_PROCESSING_MESSAGE,
} from './constants';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';
import {
	completeSetupFailure,
	completeSetupRequest,
	completeSetupSuccess,
	createDirectDebitFailure,
	createDirectDebitRequest,
	createDirectDebitSuccess,
	deleteMethodRequest,
	getBanksFailure,
	getBanksRequest,
	getBanksSuccess,
	getClientAccessFailure,
	getClientAccessRequest,
	getClientAccessSuccess,
	getPaymentMethodsFailure,
	getPaymentMethodsRequest,
	getPaymentMethodsSuccess,
	getServicesFailure,
	getServicesRequest,
	getServicesSuccess,
	getSummaryFailure,
	getSummaryRequest,
	getSummarySuccess,
	methodActionDone,
	reloadPaymentMethodsRequest,
	setDefaultRequest,
	startSetupFailure,
	startSetupRequest,
	startSetupSuccess,
	updateMethodServicesFailure,
	updateMethodServicesRequest,
	updateMethodServicesSuccess,
	verifyDirectDebitFailure,
	verifyDirectDebitRequest,
	verifyDirectDebitSuccess,
} from './reducer';
import {
	TCompleteSetupPayload,
	TDirectDebitCreateRequest,
	TDirectDebitVerifyRequest,
	TMethodIdPayload,
	TPaymentMethodSetupRequest,
	TSilentRequest,
	TUpdateMethodServicesRequest,
} from './types';

// While the binding is "processing" (3-D Secure in progress) the state is re-read every 2 s for up to 60 s.
const SETUP_POLL_INTERVAL_MS = 2_000;
const SETUP_POLL_TIMEOUT_MS = 60_000;

function* ensureFreshToken() {
	yield put(refreshTokenByTimeOutRequest());
	yield take(refreshTokenByTimeOutSuccess.type);
}

const notifyError = (error: unknown) => notificationContainer(getErrorMessage(error), 'error');

// ==========================================: list, summary, services

function* getPaymentMethodsWorker() {
	try {
		yield call(ensureFreshToken);
		const response: TPaymentMethods = yield call(api.paymentMethods.getPaymentMethods);
		yield put(getPaymentMethodsSuccess(response ?? []));
	} catch (error) {
		yield put(getPaymentMethodsFailure());
		notifyError(error);
	}
}

function* getSummaryWorker(action: PayloadAction<TSilentRequest>) {
	try {
		yield call(ensureFreshToken);
		const merchantId: string | null = yield select(getMerchantId);
		// silent = the header banner: its 401 must not sign the user out on every page
		const response: TPaymentMethodsSummary = yield call(api.paymentMethods.getSummary, {
			background: !!action.payload?.silent,
		});
		yield put(getSummarySuccess({ summary: response, merchantId: merchantId ?? null }));
	} catch (error) {
		yield put(getSummaryFailure());
		if (!action.payload?.silent) notifyError(error);
	}
}

function* getServicesWorker() {
	try {
		yield call(ensureFreshToken);
		const response: TPaymentServices = yield call(api.paymentMethods.getServices);
		yield put(getServicesSuccess(response ?? []));
	} catch (error) {
		yield put(getServicesFailure());
		notifyError(error);
	}
}

function* reloadPaymentMethodsWorker() {
	yield put(getPaymentMethodsRequest());
	yield put(getSummaryRequest());
}

// ==========================================: default, delete, services of a method

function* setDefaultWorker(action: PayloadAction<TMethodIdPayload>) {
	try {
		yield call(ensureFreshToken);
		yield call(api.paymentMethods.setDefault, action.payload.id);
		notificationContainer('Default payment method changed', 'success');
		yield put(reloadPaymentMethodsRequest());
	} catch (error) {
		notifyError(error);
	} finally {
		yield put(methodActionDone());
	}
}

function* deleteMethodWorker(action: PayloadAction<TMethodIdPayload>) {
	try {
		yield call(ensureFreshToken);
		const response: TDeleteMethodResponse = yield call(
			api.paymentMethods.deleteMethod,
			action.payload.id,
		);
		if (response?.message) {
			notificationContainer(response.message, 'info');
		} else {
			notificationContainer('Payment method deleted', 'success');
		}
		yield put(reloadPaymentMethodsRequest());
	} catch (error) {
		notifyError(error);
	} finally {
		yield put(methodActionDone());
	}
}

function* updateMethodServicesWorker(action: PayloadAction<TUpdateMethodServicesRequest>) {
	try {
		yield call(ensureFreshToken);
		yield call(api.paymentMethods.updateMethodServices, action.payload);
		yield put(updateMethodServicesSuccess());
		notificationContainer('Services updated', 'success');
		yield put(getPaymentMethodsRequest());
	} catch (error) {
		const message = getErrorMessage(error);
		yield put(updateMethodServicesFailure(message));
		notificationContainer(message, 'error');
	}
}

// ==========================================: binding through Drop-in

function* startSetupWorker(action: PayloadAction<TPaymentMethodSetupRequest>) {
	try {
		yield call(ensureFreshToken);
		const response: TPaymentMethodSetup = yield call(api.paymentMethods.startSetup, action.payload);
		yield put(startSetupSuccess(response));
	} catch (error) {
		const message = getErrorMessage(error);
		yield put(startSetupFailure(message));
		notificationContainer(message, 'error');
	}
}

const notifySetupResult = (result: TSetupResult) => {
	switch (result.status) {
		case 'completed':
			notificationContainer(SETUP_COMPLETED_MESSAGE, 'success');
			break;
		case 'duplicate':
			notificationContainer(SETUP_DUPLICATE_MESSAGE, 'info');
			break;
		case 'processing':
			notificationContainer(SETUP_PROCESSING_MESSAGE, 'info');
			break;
		default:
			notificationContainer(result.message || 'The payment method was not added.', 'error');
	}
};

function* completeSetupWorker(action: PayloadAction<TCompleteSetupPayload>) {
	const { notify, ...request } = action.payload;
	try {
		yield call(ensureFreshToken);
		let result: TSetupResult = yield call(api.paymentMethods.completeSetup, request);

		const startedAt = Date.now();
		while (result?.status === 'processing' && Date.now() - startedAt < SETUP_POLL_TIMEOUT_MS) {
			yield delay(SETUP_POLL_INTERVAL_MS);
			yield call(ensureFreshToken);
			result = yield call(api.paymentMethods.getSetup, request.setupId);
		}

		yield put(completeSetupSuccess(result));
		if (notify) notifySetupResult(result);
		if (result?.status === 'completed' || result?.status === 'duplicate') {
			yield put(reloadPaymentMethodsRequest());
		}
	} catch (error) {
		const message = getErrorMessage(error);
		yield put(completeSetupFailure(message));
		notificationContainer(message, 'error');
	}
}

// ==========================================: Direct Debit mandate

function* getBanksWorker() {
	try {
		yield call(ensureFreshToken);
		const response: TDirectDebitBanks = yield call(api.paymentMethods.getBanks);
		yield put(getBanksSuccess(response ?? []));
	} catch (error) {
		yield put(getBanksFailure());
		notifyError(error);
	}
}

function* createDirectDebitWorker(action: PayloadAction<TDirectDebitCreateRequest>) {
	try {
		yield call(ensureFreshToken);
		const response: TDirectDebitCreateResponse = yield call(
			api.paymentMethods.createDirectDebit,
			action.payload,
		);
		yield put(createDirectDebitSuccess(response));
	} catch (error) {
		yield put(createDirectDebitFailure());
		notifyError(error);
	}
}

function* verifyDirectDebitWorker(action: PayloadAction<TDirectDebitVerifyRequest>) {
	try {
		yield call(ensureFreshToken);
		const response: TDirectDebitVerifyResult = yield call(
			api.paymentMethods.verifyDirectDebit,
			action.payload,
		);
		yield put(verifyDirectDebitSuccess(response));
		yield put(reloadPaymentMethodsRequest());
	} catch (error) {
		yield put(verifyDirectDebitFailure());
		notifyError(error);
	}
}

// ==========================================: operator restrictions (client access)

// Loaded by the payment methods page and the Make payment entry points, and again after a 423 of a blocked call
// (services/http/interceptor/onError). Silent: without the state nothing is blocked in the UI.
function* getClientAccessWorker() {
	try {
		yield call(ensureFreshToken);
		const response: TClientAccess = yield call(api.paymentMethods.getClientAccess);
		yield put(getClientAccessSuccess(response));
	} catch {
		yield put(getClientAccessFailure());
	}
}

export function* paymentMethodsSaga() {
	yield takeLatest(getPaymentMethodsRequest.type, getPaymentMethodsWorker);
	yield takeLatest(getSummaryRequest.type, getSummaryWorker);
	yield takeLatest(getServicesRequest.type, getServicesWorker);
	yield takeEvery(reloadPaymentMethodsRequest.type, reloadPaymentMethodsWorker);
	yield takeEvery(setDefaultRequest.type, setDefaultWorker);
	yield takeEvery(deleteMethodRequest.type, deleteMethodWorker);
	yield takeLatest(updateMethodServicesRequest.type, updateMethodServicesWorker);
	yield takeLatest(startSetupRequest.type, startSetupWorker);
	yield takeLatest(completeSetupRequest.type, completeSetupWorker);
	yield takeLatest(getBanksRequest.type, getBanksWorker);
	yield takeLatest(createDirectDebitRequest.type, createDirectDebitWorker);
	yield takeLatest(verifyDirectDebitRequest.type, verifyDirectDebitWorker);
	yield takeLatest(getClientAccessRequest.type, getClientAccessWorker);
}
